import { ButtonProps, IconButton } from '@mui/material';
import { DynamicDefaultProps, colorToRGBA } from '@synergy/utils';
import { PoolDynamicDefaultPropsComponent } from '@synergy/abstract';
import { Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

const styles = (theme: Theme) =>
    createStyles({
        root: {
            backgroundColor: '#fff',
            width: 34,
            height: 34,
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: `0px 2px 2px ${theme.palette.grey[400]}`,
        },
        disabled: {
            color: '#999999',
            '& svg': {
                color: '#999999',
            },
        },
        firstLevel: {},
        secondLevel: {
            '&:hover': {
                backgroundColor: theme.palette.grey[100],
            },
        },
        thirdLevel: {
            borderRadius: theme.spacing(3),
            backgroundColor: '#F1F1F1',

            '&:disabled': {
                backgroundColor: 'transparent',
            },

            '&:hover': {
                backgroundColor: theme.palette.common.white,
                boxShadow: '0 2px 16px 0 rgba(0,0,0,0.5)', // magic
            },
        },
        link: {
            '&:hover': {
                backgroundColor: theme.palette.common.white,
                boxShadow: '0 2px 26px 0 rgba(0, 0, 0, .5)',
            },

            '&:not([disabled])': {
                borderRadius: '2rem',
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.grey[100],

                '& .material-icons': {
                    color: theme.palette.primary.main,
                },
            },

            '& .material-icons:first-child': {
                marginRight: theme.spacing(),
            },
        },
        toolbar: {
            color: theme.palette.common.white,
            borderRadius: 0,

            '&:disabled, &:disabled svg': {
                color: '#7DC7D4', // magic
            },

            '&:hover': {
                backgroundColor: colorToRGBA(theme.palette.common.white, 0.2),
            },

            '& .material-icons': {
                marginRight: theme.spacing(),
            },
        },
    });

interface PoolIconButtonBaseProps extends WithStyles<typeof styles> {
    styleType?: keyof ReturnType<typeof styles>;
}

type PoolIconButtonProps = PoolIconButtonBaseProps & ButtonProps;

export const PoolIconButton = withStyles(styles)(
    class PoolIconButtonBase extends PoolDynamicDefaultPropsComponent<PoolIconButtonProps> {
        protected override dynamicDefaultProps: DynamicDefaultProps<PoolIconButtonProps> = {
            firstLevel: {
                color: 'primary',
                variant: 'contained',
            },
            secondLevel: {
                color: 'primary',
            },
            thirdLevel: {},
            link: {
                color: 'primary',
            },
        };

        private static getDisplayClass(props: PoolIconButtonProps): string {
            if (props.styleType) {
                return props.classes[props.styleType];
            }

            return '';
        }

        private static getClassNames(props: PoolIconButtonProps) {
            return `${this.getDisplayClass(props)} ${props.classes.root} ${props.className || ''}`;
        }

        public override render() {
            const { defaultProps } = this.state;
            const { styleType: _styleType, classes, ...baseButtonProps } = this.props;
            const props = { ...defaultProps, ...this.props };

            return (
                <IconButton
                    {...defaultProps}
                    {...baseButtonProps}
                    classes={{ disabled: classes.disabled }}
                    className={PoolIconButtonBase.getClassNames(props)}
                >
                    {props.children}
                </IconButton>
            );
        }
    }
);
